import React from 'react';
import {
  BEHAVIOUR_TYPES,
  RATE_SOURCES,
  ROUNDING_OPTIONS,
  humanReadableFormula,
  DEPARTMENT_FY_RATE_KEYS,
} from '../../adapters';
import FormulaBlockBuilder from '../FormulaBlockBuilder';

const fieldOptions = (fields, selfCode) =>
  (fields || [])
    .filter((f) => f.fieldCode !== selfCode && String(f.status || 'ACTIVE') === 'ACTIVE')
    .map((f) => ({ value: f.fieldCode, label: f.fieldLabel || f.fieldCode }));

const FieldSelect = ({ label, value, options, onChange, readOnly, help }) => (
  <div className="form-group">
    <label>{label}</label>
    <select disabled={readOnly} value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">— Select —</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    {help ? <p className="ra-tpl-help">{help}</p> : null}
  </div>
);

const CalculationTab = ({ field, fields, readOnly, onChange }) => {
  const cfg = field.metadata?.businessConfig || {};
  const opts = fieldOptions(fields, field.fieldCode);
  const patchCfg = (patch) => onChange({
    metadata: {
      ...(field.metadata || {}),
      businessConfig: { ...cfg, ...patch },
    },
  });
  const behaviour = cfg.behaviourType || BEHAVIOUR_TYPES.MANUAL_ENTRY;

  return (
    <div className="ra-tpl-tab-body">
      <div className="ra-tpl-calc-preview">
        <label>Calculation Preview</label>
        <pre>{humanReadableFormula(field, fields)}</pre>
      </div>

      {(behaviour === BEHAVIOUR_TYPES.MANUAL_ENTRY || behaviour === BEHAVIOUR_TYPES.REFERENCE_ONLY || behaviour === BEHAVIOUR_TYPES.WITHHELD || behaviour === BEHAVIOUR_TYPES.RELEASE) && (
        <div className="form-row">
          <div className="form-group">
            <label>Default Value</label>
            <input
              disabled={readOnly}
              type="number"
              value={field.defaultValue ?? ''}
              onChange={(e) => onChange({ defaultValue: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </div>
          <div className="form-group">
            <label>Min</label>
            <input disabled={readOnly} type="number" value={field.minValue ?? ''} onChange={(e) => onChange({ minValue: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
          <div className="form-group">
            <label>Max</label>
            <input disabled={readOnly} type="number" value={field.maxValue ?? ''} onChange={(e) => onChange({ maxValue: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
        </div>
      )}

      {behaviour === BEHAVIOUR_TYPES.PERCENTAGE && (
        <>
          <FieldSelect label="Calculated On" value={cfg.baseFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ baseFieldCode: v })} help="The amount this percentage uses as its base." />
          <div className="form-row">
            <div className="form-group">
              <label>Rate Source</label>
              <select disabled={readOnly} value={cfg.rateSource || 'FIXED'} onChange={(e) => patchCfg({ rateSource: e.target.value })}>
                {RATE_SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Rate (%){String(cfg.rateSource || '').toUpperCase() === 'DEPARTMENT' ? ' fallback' : ''}</label>
              <input disabled={readOnly} type="number" step="0.01" value={cfg.rateValue ?? ''} onChange={(e) => patchCfg({ rateValue: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
          </div>
          {String(cfg.rateSource || '').toUpperCase() === 'DEPARTMENT' ? (
            <FieldSelect
              label="Department FY Rate Field"
              value={cfg.rateFieldCode}
              options={DEPARTMENT_FY_RATE_KEYS}
              readOnly={readOnly}
              onChange={(v) => patchCfg({ rateFieldCode: v })}
              help="Pulled from Admin → Departments → FY recovery rates for the bill date."
            />
          ) : null}
        </>
      )}

      {behaviour === BEHAVIOUR_TYPES.CAPPED_PERCENTAGE && (
        <>
          <FieldSelect label="Calculated On" value={cfg.baseFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ baseFieldCode: v })} />
          <div className="form-row">
            <div className="form-group">
              <label>Rate Source</label>
              <select disabled={readOnly} value={cfg.rateSource || 'FIXED'} onChange={(e) => patchCfg({ rateSource: e.target.value })}>
                {RATE_SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Rate (%){String(cfg.rateSource || '').toUpperCase() === 'DEPARTMENT' ? ' fallback' : ''}</label>
              <input disabled={readOnly} type="number" step="0.01" value={cfg.rateValue ?? ''} onChange={(e) => patchCfg({ rateValue: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
            <div className="form-group">
              <label>Maximum Recovery</label>
              <input disabled={readOnly} type="number" step="0.01" value={cfg.capValue ?? ''} onChange={(e) => patchCfg({ capValue: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
          </div>
          {String(cfg.rateSource || '').toUpperCase() === 'DEPARTMENT' ? (
            <FieldSelect
              label="Department FY Rate Field"
              value={cfg.rateFieldCode}
              options={DEPARTMENT_FY_RATE_KEYS}
              readOnly={readOnly}
              onChange={(v) => patchCfg({ rateFieldCode: v })}
              help="Pulled from Admin → Departments → FY recovery rates for the bill date."
            />
          ) : null}
          <FieldSelect label="Maximum Based On (optional % base)" value={cfg.capBaseFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ capBaseFieldCode: v })} />
        </>
      )}

      {(behaviour === BEHAVIOUR_TYPES.SUM_OF_FIELDS || behaviour === BEHAVIOUR_TYPES.AUTO_TOTAL) && (
        <div className="form-group">
          <label>{behaviour === BEHAVIOUR_TYPES.AUTO_TOTAL ? 'Auto-total from Include In group' : 'Fields to Include'}</label>
          {behaviour === BEHAVIOUR_TYPES.AUTO_TOTAL ? (
            <p className="ra-tpl-help">Members are collected automatically from fields with matching Include In. Save regenerates the total.</p>
          ) : (
            <div className="ra-bg-check-list ra-tpl-check-list">
              {opts.map((o) => {
                const checked = (cfg.sumFieldCodes || []).includes(o.value);
                return (
                  <label key={o.value}>
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={checked}
                      onChange={(e) => {
                        const set = new Set(cfg.sumFieldCodes || []);
                        if (e.target.checked) set.add(o.value);
                        else set.delete(o.value);
                        patchCfg({ sumFieldCodes: Array.from(set) });
                      }}
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {behaviour === BEHAVIOUR_TYPES.SUBTRACT_FIELDS && (
        <div className="ra-tpl-subtract-visual">
          <FieldSelect label="Starting amount" value={(cfg.subtractFieldCodes || [])[0] || cfg.minuendFieldCode} options={opts} readOnly={readOnly} onChange={(v) => {
            const rest = (cfg.subtractFieldCodes || []).slice(1);
            patchCfg({ minuendFieldCode: v, subtractFieldCodes: v ? [v, ...rest] : rest });
          }} />
          <div className="ra-tpl-op-divider">−</div>
          <div className="form-group">
            <label>Subtract</label>
            <div className="ra-tpl-check-list">
              {opts.map((o) => {
                const codes = cfg.subtractFieldCodes || [];
                const checked = codes.slice(1).includes(o.value) || (cfg.sumFieldCodes || []).includes(o.value);
                return (
                  <label key={o.value}>
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={checked}
                      onChange={(e) => {
                        const minuend = codes[0] || cfg.minuendFieldCode;
                        const rest = new Set(codes.slice(1).length ? codes.slice(1) : (cfg.sumFieldCodes || []));
                        if (e.target.checked) rest.add(o.value);
                        else rest.delete(o.value);
                        const next = minuend ? [minuend, ...rest] : Array.from(rest);
                        patchCfg({ subtractFieldCodes: next, sumFieldCodes: Array.from(rest), minuendFieldCode: minuend });
                      }}
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {behaviour === BEHAVIOUR_TYPES.QUANTITY_RATE && (
        <>
          <FieldSelect label="Quantity Field" value={cfg.quantityFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ quantityFieldCode: v })} />
          <FieldSelect label="Rate Field" value={cfg.rateFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ rateFieldCode: v })} />
          <div className="form-group">
            <label>Or fixed rate</label>
            <input disabled={readOnly} type="number" value={cfg.rateValue ?? ''} onChange={(e) => patchCfg({ rateValue: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
        </>
      )}

      {behaviour === BEHAVIOUR_TYPES.BALANCE_RECOVERY && (
        <>
          <FieldSelect label="Original Advance" value={cfg.originalAdvanceFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ originalAdvanceFieldCode: v })} />
          <FieldSelect label="Previous Outstanding" value={cfg.previousOutstandingFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ previousOutstandingFieldCode: v })} />
          <FieldSelect label="Calculated On (current recovery base)" value={cfg.baseFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ baseFieldCode: v })} />
          <div className="form-row">
            <div className="form-group">
              <label>Recovery Method</label>
              <select disabled={readOnly} value={cfg.recoveryMethod || 'PERCENTAGE'} onChange={(e) => patchCfg({ recoveryMethod: e.target.value })}>
                <option value="PERCENTAGE">Percentage</option>
                <option value="FIXED">Fixed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Recovery Rate</label>
              <input disabled={readOnly} type="number" step="0.01" value={cfg.rateValue ?? ''} onChange={(e) => patchCfg({ rateValue: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
          </div>
          <label className="ra-tpl-inline-check">
            <input type="checkbox" disabled={readOnly} checked={cfg.capToOutstanding !== false} onChange={(e) => patchCfg({ capToOutstanding: e.target.checked })} />
            Do not recover more than outstanding balance
          </label>
        </>
      )}

      {behaviour === BEHAVIOUR_TYPES.CONDITIONAL && (
        <div className="form-group">
          <label>Conditions</label>
          <p className="ra-tpl-help">Apply this field when all conditions match. Otherwise use the else value.</p>
          {(cfg.conditionTree?.conditions || []).map((cond, idx) => (
            <div key={idx} className="ra-tpl-condition-row">
              <select disabled={readOnly} value={cond.leftFieldCode || ''} onChange={(e) => {
                const conditions = [...(cfg.conditionTree?.conditions || [])];
                conditions[idx] = { ...cond, leftFieldCode: e.target.value };
                patchCfg({ conditionTree: { logic: cfg.conditionTree?.logic || 'AND', conditions } });
              }}>
                <option value="">Field</option>
                {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select disabled={readOnly} value={cond.operator || 'EQUAL'} onChange={(e) => {
                const conditions = [...(cfg.conditionTree?.conditions || [])];
                conditions[idx] = { ...cond, operator: e.target.value };
                patchCfg({ conditionTree: { logic: cfg.conditionTree?.logic || 'AND', conditions } });
              }}>
                <option value="EQUAL">equals</option>
                <option value="NOT_EQUAL">not equals</option>
                <option value="GREATER_THAN">greater than</option>
                <option value="LESS_THAN">less than</option>
              </select>
              <input disabled={readOnly} value={cond.rightValue ?? ''} onChange={(e) => {
                const conditions = [...(cfg.conditionTree?.conditions || [])];
                const val = e.target.value;
                conditions[idx] = { ...cond, rightValue: Number.isNaN(Number(val)) ? val : Number(val) };
                patchCfg({ conditionTree: { logic: cfg.conditionTree?.logic || 'AND', conditions } });
              }} />
            </div>
          ))}
          {!readOnly && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => patchCfg({
                conditionTree: {
                  logic: 'AND',
                  conditions: [...(cfg.conditionTree?.conditions || []), { leftFieldCode: '', operator: 'EQUAL', rightValue: '' }],
                },
              })}
            >
              + Add Condition
            </button>
          )}
          <div className="form-group" style={{ marginTop: 10 }}>
            <label>Otherwise</label>
            <input disabled={readOnly} type="number" value={cfg.elseValue ?? 0} onChange={(e) => patchCfg({ elseValue: Number(e.target.value) || 0 })} />
          </div>
          <FieldSelect label="Then use field" value={cfg.thenFieldCode || cfg.baseFieldCode} options={opts} readOnly={readOnly} onChange={(v) => patchCfg({ thenFieldCode: v, baseFieldCode: v })} />
        </div>
      )}

      {(behaviour === BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE || behaviour === BEHAVIOUR_TYPES.CURRENT_MINUS_PREVIOUS) && (
        <FieldSelect
          label={behaviour === BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE ? 'Previous Field' : 'Base Field'}
          value={cfg.previousFieldCode || cfg.baseFieldCode || field.fieldCode}
          options={[{ value: field.fieldCode, label: `${field.fieldLabel || field.fieldCode} (this field)` }, ...opts]}
          readOnly={readOnly}
          onChange={(v) => patchCfg(behaviour === BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE ? { previousFieldCode: v } : { baseFieldCode: v, previousFieldCode: v })}
        />
      )}

      {behaviour === BEHAVIOUR_TYPES.CUSTOM_FORMULA && (
        <FormulaBlockBuilder
          readOnly={readOnly}
          fields={fields}
          blocks={cfg.formulaBlocks || []}
          formulaJson={field.formulaJson}
          onChangeBlocks={(formulaBlocks) => patchCfg({ formulaBlocks })}
        />
      )}

      <div className="form-group">
        <label>Rounding</label>
        <select disabled={readOnly} value={field.roundingMethod || 'ROUND'} onChange={(e) => onChange({ roundingMethod: e.target.value })}>
          {ROUNDING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
};

export default CalculationTab;
